import { SORT_OPTIONS } from "../../../../constants";

export const filterCVs = ({ cvs = [], searchObject = {}, labels = [], sort = SORT_OPTIONS.DESC }) => {
  // Early return for empty array
  if (!cvs?.length) return [];

  const {
    keyword = "",
    selectedLabels = [],
    uploadDate: {
      from: uploadDateFrom = "",
      to: uploadDateTo = ""
    } = {},
    occupation: {
      activitySector: activitySectorSearchOptions = [],
      occupation: occupationSearchOptions = []
    } = {},
    location: {
      country: countrySearchOptions = [],
      city: citySearchOptions = []
    } = {},
    skills: {
      // softSkills: softSkillsSearchOptions = [], // Not used right now
      hardSkills: hardSkillsSearchOptions = []
    } = {}
  } = searchObject;

  // Quickly check if any filters are active
  const hasKeyword = Boolean(keyword);
  const hasDateFilter = Boolean(uploadDateFrom || uploadDateTo);
  const hasSelectedLabelsFilter = selectedLabels.length > 0;
  const hasActivitySectorFilter = activitySectorSearchOptions.length > 0;
  const hasOccupationFilter = occupationSearchOptions.length > 0;
  const hasCountryFilter = countrySearchOptions.length > 0;
  const hasCityFilter = citySearchOptions.length > 0;
  const hasHardSkillsFilter = hardSkillsSearchOptions.length > 0;

  const filtersActive = hasKeyword || hasDateFilter || hasSelectedLabelsFilter || 
                        hasActivitySectorFilter || hasOccupationFilter || 
                        hasCountryFilter || hasCityFilter || hasHardSkillsFilter;

  if (!filtersActive) {
    if (sort === SORT_OPTIONS.DESC) {
      return [...cvs].reverse();
    }
    return cvs;
  }

  const keywordLowered = keyword ? keyword.toLowerCase() : "";

  const fromTime = uploadDateFrom ? new Date(`${uploadDateFrom}T00:00:00`).getTime() : null;
  const toTime = uploadDateTo ? new Date(`${uploadDateTo}T23:59:59`).getTime() : new Date().getTime();

  // Pre-calculate lookup maps for faster matching
  const activitySectorMap = new Set(
    activitySectorSearchOptions.map(option => option.value.toLowerCase())
  );
  const occupationMap = new Set(
    occupationSearchOptions.map(option => option.value.toLowerCase())
  );
  const cityMap = new Set(
    citySearchOptions.map(option => option.value.toLowerCase())
  );
  const countryMap = new Set(
    countrySearchOptions.map(option => option.value.toLowerCase())
  );
  const hardSkillsMap = new Set(
    hardSkillsSearchOptions.map(option => option.value.toLowerCase())
  );

  // Cache map sizes to avoid repeated property access
  const hasActivitySectorMap = activitySectorMap.size > 0;
  const hasOccupationMap = occupationMap.size > 0;
  const hasCityMap = cityMap.size > 0;
  const hasCountryMap = countryMap.size > 0;
  const hasHardSkillsMap = hardSkillsMap.size > 0;

  const filteredCVs = cvs.reduce((acc, cv) => {
    // Quick date filter first - saves processing if outside range
    if (hasDateFilter) {
      const createdTime = new Date(cv.created_at).getTime();

      // Outside of range check
      if ((fromTime && createdTime < fromTime) || createdTime > toTime) {
        return acc;
      }
    }

    const {
      education,
      experience,
      soft_skills,
      hard_skills,
      information: { address, first_name, last_name, file_name }
    } = cv;

    // Pre-process values outside of conditions
    const fullNameLowered = hasKeyword ? 
      `${first_name?.toLowerCase() || ''}${last_name ? ` ${last_name.toLowerCase()}` : ""}` : "";
    const fileNameLowered = hasKeyword ? file_name?.toLowerCase() || '' : '';
    const cityLowered = (hasKeyword || hasCityMap) ? address?.city?.toLowerCase() || '' : '';
    const countryLowered = (hasKeyword || hasCountryMap) ? address?.country?.toLowerCase() || '' : '';

    // Keyword filters behave like OR, only one needs to match
    const orFilters = hasKeyword ? {
      fullName: fullNameLowered?.includes(keywordLowered),
      fileName: fileNameLowered?.includes(keywordLowered),

      education: education?.some(item => 
        item.degree?.toLowerCase().includes(keywordLowered)
      ) || false,

      experience: experience?.some(item =>
        item.title?.toLowerCase()?.includes(keywordLowered)
      ) || false,

      city: cityLowered?.includes(keywordLowered) || false,
      country: countryLowered?.includes(keywordLowered) || false,

      softSkills: soft_skills?.some(item => 
        item.skill?.toLowerCase().includes(keywordLowered)
      ) || false,
      hardSkills: hard_skills?.some(item =>
        item.skill?.toLowerCase().includes(keywordLowered)
      ) || false,
    } : { true: true }; // Default to true if no keyword

    // All other filters behave like AND, all must match
    const andFilters = {
      ...(hasSelectedLabelsFilter ? {
        selectedLabels: selectedLabels.every(selectedLabel => {
          const fullLabel = labels.find(label => label.id === selectedLabel.value);
          return fullLabel?.cvLabels?.includes(cv.id);
        })
      } : {}),

      ...(hasActivitySectorMap ? {
        activitySector: experience?.some(item =>
          item.activity_sector && activitySectorMap.has(item.activity_sector.toLowerCase())
        )
      } : {}),

      ...(hasOccupationMap ? {
        occupation: experience?.some(item =>
          item.title && occupationMap.has(item.title.toLowerCase())
        )
      } : {}),

      ...(hasCityMap ? {
        city: cityLowered && cityMap.has(cityLowered)
      } : {}),

      ...(hasCountryMap ? {
        country: countryLowered && countryMap.has(countryLowered)
      } : {}),

      ...(hasHardSkillsMap ? {
        hardSkills: hard_skills?.some(item =>
          item.skill && hardSkillsMap.has(item.skill.toLowerCase())
        )
      } : {})
    };

    if (
      Object.values(orFilters).some(Boolean) &&
      Object.values(andFilters).every(Boolean)
    ) {
      acc.push({
        ...cv,
        filters: {
          ...orFilters,
          ...andFilters,
        }
      });
    }

    return acc;
  }, []);

  // Apply sorting only once at the end
  if (sort === SORT_OPTIONS.DESC) {
    filteredCVs.reverse();
  }

  return filteredCVs;
};
